import { useEffect, useRef, useState } from 'react';

// audit AD-10: autosave a form's in-progress values to localStorage (debounced) so an accidental
// refresh/close doesn't lose everything typed, with a visible "Draft saved" timestamp and an
// explicit way to discard it. Deliberately separate from TestCreateWizard's own server-persisted,
// multi-step draft flow (a different, already-shipped mechanism this must not touch) — this is a
// lightweight, purely-local safety net for shorter single-screen forms (Add/Edit Student,
// announcement composer, etc).
const PREFIX = 'zaptude_draft_';

function readDraft(key) {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || !parsed.savedAt) return null;
    return parsed; // { data, savedAt }
  } catch {
    return null;
  }
}

/**
 * @param {string} key   unique per form (e.g. 'add_student', `edit_student_${id}`)
 * @param {object} values the current form values to persist
 * @param {object} opts  { enabled: only autosave while true (e.g. the form is dirty), debounceMs }
 * @returns {{ initialDraft: {data,savedAt}|null, savedAt: string|null, clearDraft: () => void }}
 */
export default function useFormDraft(key, values, { enabled = true, debounceMs = 800 } = {}) {
  const [initialDraft] = useState(() => readDraft(key)); // read once on mount — a fresh save below must not re-trigger this
  const [savedAt, setSavedAt] = useState(null);
  const timer = useRef(null);
  const serialized = JSON.stringify(values);

  useEffect(() => {
    if (!enabled) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const savedAtIso = new Date().toISOString();
      try {
        localStorage.setItem(PREFIX + key, JSON.stringify({ data: values, savedAt: savedAtIso }));
        setSavedAt(savedAtIso);
      } catch {
        // Storage full/unavailable (private browsing, quota) — autosave is best-effort and must
        // never block the form itself.
      }
    }, debounceMs);
    return () => clearTimeout(timer.current);
    // `values` is intentionally compared by its serialized form so callers can pass a plain
    // object literal each render without memoizing it themselves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, serialized, debounceMs]);

  const clearDraft = () => {
    try { localStorage.removeItem(PREFIX + key); } catch { /* ignore */ }
    setSavedAt(null);
  };

  return { initialDraft, savedAt, clearDraft };
}
